/**
 * What a Guesty listing implies about money, with none of Guesty's data attached.
 *
 * WHY THIS IS ITS OWN FILE NOW
 *
 * These four functions lived beside the frozen snapshot, and importing any one of them imported
 * 2.8 MB of base64 photographs with it — into the bundle every person downloads, for the sake of a
 * twelve-line multiplication. Nobody would have noticed; it is exactly the kind of weight a
 * product accumulates without anybody deciding to carry it.
 *
 * So the snapshot stays in `data.js`, where the tests and Storybook want it, and the arithmetic
 * lives here, where the running product wants it. Neither knows about the other.
 */

/**
 * Guesty holds no monthly rent. It holds a nightly `basePrice` and a `monthlyPriceFactor`, and
 * multiplies — which is the model PRICING_RULES exists to reject.
 *
 * Measured across the 126 active listings, the factor is not a policy either: 0.75 on 35, 0.80 on
 * 60, 0.85 on 3, 0.90 on 27, and 1.00 on one home, which therefore charges a month at thirty times
 * its nightly rate. Those are settings somebody typed once, not a pricing rule.
 *
 * So the derived figure SEEDS a monthly rent and is labelled as derived. What it must never do is
 * masquerade as a rent somebody set. Deciding who owns the real monthly rent is a Product Owner
 * question that has not been asked yet, and printing this number without the label would quietly
 * answer it.
 */
export function seedMonthlyRent(unit) {
  const { basePrice, monthlyPriceFactor } = unit.prices ?? {}
  if (typeof basePrice !== 'number' || typeof monthlyPriceFactor !== 'number') return null
  return Math.round(basePrice * 30 * monthlyPriceFactor)
}

/**
 * P-02's floor. Ninety per cent of the monthly rent, matching the contract's worked examples
 * ($3,000 rent, $2,700 minimum) until a real per-unit floor exists.
 */
export const seedMinimum = (unit) => {
  const rent = seedMonthlyRent(unit)
  return rent === null ? null : Math.round(rent * 0.9)
}

/**
 * The one-time fees, from the listing itself.
 *
 * `cleaningFee` is absent on one of the 126 active listings and `petFee` on 21 of them. An absent
 * fee is NOT a fee of zero — P-05 forbids waiving one, and a missing value silently becoming £0
 * would waive it by accident. Absent means "not configured", and it is returned as such.
 */
export function unitFees(unit) {
  const p = unit.prices ?? {}
  const fees = []
  if (typeof p.cleaningFee === 'number') fees.push({ label: 'Cleaning', amount: p.cleaningFee })
  else fees.push({ label: 'Cleaning', amount: null, notConfigured: true })
  return fees
}

/** Amenities on every home cannot filter anything; nor can ones on almost none. */
export function discriminatingAmenities(units, { floor = 0.1, ceiling = 0.9 } = {}) {
  const counts = new Map()
  for (const u of units) for (const a of u.amenities ?? []) counts.set(a, (counts.get(a) ?? 0) + 1)
  const n = units.length
  return [...counts.entries()]
    .filter(([, k]) => k >= floor * n && k <= ceiling * n)
    .sort((a, b) => b[1] - a[1])
    .map(([amenity, count]) => ({ amenity, count }))
}

